/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { keyframes } from '@instructure/emotion';
// keyframes have to be outside of 'generateStyle',
// since it is causing problems in style recalculation
const pulseAnimation = keyframes`
  to {
    transform: scale(1);
    opacity: 0.9;
  }`;

/**
 * ---
 * private: true
 * ---
 * Generates the style object from the theme and provided additional information
 * @param  {Object} componentTheme The theme variable object.
 * @param  {Object} props the props of the component, the style is applied to
 * @param  {Object} state the state of the component, the style is applied to
 * @return {Object} The final style object, which will be used in the component
 */
const generateStyle = (componentTheme, props) => {
  const type = props.type,
    variant = props.variant,
    _props$placement = props.placement,
    placement = _props$placement === void 0 ? '' : _props$placement,
    standalone = props.standalone,
    pulse = props.pulse;
  const top = placement.indexOf('top') > -1;
  const bottom = placement.indexOf('bottom') > -1;
  const start = placement.indexOf('start') > -1;
  const end = placement.indexOf('end') > -1;
  const center = placement.indexOf('center') > -1;
  const variants = {
    danger: {
      badge: {
        color: componentTheme.color,
        backgroundColor: componentTheme.colorDanger
      },
      pulseBorder: {
        borderColor: componentTheme.colorDanger
      }
    },
    success: {
      badge: {
        color: componentTheme.color,
        backgroundColor: componentTheme.colorSuccess
      },
      pulseBorder: {
        borderColor: componentTheme.colorSuccess
      }
    },
    primary: {
      badge: {
        color: componentTheme.color,
        backgroundColor: componentTheme.colorPrimary
      },
      pulseBorder: {
        borderColor: componentTheme.colorPrimary
      }
    },
    inverse: {
      // text and background colors are swapped
      badge: {
        color: componentTheme.colorInverse,
        backgroundColor: componentTheme.color
      },
      pulseBorder: {
        borderColor: componentTheme.color
      }
    }
  };
  const countPositions = {
    ...(top && {
      top: `calc(-1 * ${componentTheme.countOffset})`
    }),
    ...(bottom && {
      bottom: `calc(-1 * ${componentTheme.countOffset})`
    }),
    ...(start && {
      insetInlineStart: `calc(-1 * ${componentTheme.countOffset})`,
      insetInlineEnd: 'auto'
    }),
    ...(end && {
      insetInlineEnd: `calc(-1 * ${componentTheme.countOffset})`,
      insetInlineStart: 'auto'
    }),
    ...(center && {
      ...((end || start) && {
        top: `calc(50% - (${componentTheme.size} / 2))`
      }),
      ...(start && {
        insetInlineStart: 'auto',
        insetInlineEnd: `calc(100% - ${componentTheme.countOffset})`
      }),
      ...(end && {
        insetInlineEnd: 'auto',
        insetInlineStart: `calc(100% - ${componentTheme.countOffset})`
      })
    })
  };
  const notificationPositions = {
    ...(top && {
      top: componentTheme.notificationOffset
    }),
    ...(bottom && {
      bottom: componentTheme.notificationOffset
    }),
    ...(start && {
      insetInlineStart: componentTheme.notificationOffset,
      insetInlineEnd: 'auto'
    }),
    ...(end && {
      insetInlineEnd: componentTheme.notificationOffset,
      insetInlineStart: 'auto'
    }),
    ...(center && {
      ...((end || start) && {
        top: `calc(50% - (${componentTheme.sizeNotification} / 2))`
      }),
      ...(start && {
        insetInlineStart: `calc(-1 * ${componentTheme.sizeNotification} / 2)`,
        insetInlineEnd: 'auto'
      }),
      ...(end && {
        insetInlineEnd: `calc(-1 * ${componentTheme.sizeNotification} / 2)`,
        insetInlineStart: 'auto'
      })
    })
  };
  const notStandaloneTypeVariant = {
    count: countPositions,
    notification: notificationPositions
  };
  const typeVariant = {
    count: {
      lineHeight: componentTheme.size,
      minWidth: componentTheme.size,
      paddingInlineStart: componentTheme.padding,
      paddingInlineEnd: componentTheme.padding
    },
    notification: {
      width: componentTheme.sizeNotification,
      height: componentTheme.sizeNotification
    }
  };
  return {
    badge: {
      label: 'badge',
      fontFamily: componentTheme.fontFamily,
      fontWeight: componentTheme.fontWeight,
      boxSizing: 'border-box',
      pointerEvents: 'none',
      textAlign: 'center',
      fontSize: componentTheme.fontSize,
      whiteSpace: 'nowrap',
      borderRadius: componentTheme.borderRadius,
      ...variants[variant].badge,
      ...(pulse && {
        position: 'relative',
        '&::before': {
          content: '""',
          width: 'calc(100% + 0.5rem)',
          height: 'calc(100% + 0.5rem)',
          borderRadius: componentTheme.borderRadius,
          position: 'absolute',
          top: '-0.25rem',
          insetInlineEnd: 'auto',
          insetInlineStart: '-0.25rem',
          boxSizing: 'border-box',
          border: `${componentTheme.pulseBorderThickness} solid`,
          opacity: 0,
          transform: 'scale(0.75)',
          animationName: pulseAnimation,
          animationDuration: '1s',
          animationIterationCount: '4',
          animationDirection: 'alternate',
          ...variants[variant].pulseBorder
        }
      }),
      ...typeVariant[type],
      ...(!standalone && {
        position: 'absolute',
        zIndex: componentTheme.notificationZIndex,
        ...notStandaloneTypeVariant[type]
      })
    },
    wrapper: {
      label: 'badge__wrapper',
      position: 'relative',
      boxSizing: 'border-box',
      svg: {
        display: 'block'
      }
    }
  };
};
export default generateStyle;